import React from "react";

import Typography from "@mui/material/Typography";

import useUpdateClips from "@app/analytics/Home/hooks/useUpdateClips";
import type { KeyQuestionAnswerFeedItem } from "@app/analytics/Home/types";
import { isProposedAnswer } from "@app/analytics/utils";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { KeyQuestionAnswerClipStatus } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { Link } from "@rui/Link";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import { Bold } from "@rui/styles";

import DetailsPanelHeader from "./DetailsPanelHeader";
import {
  ActionsMenuButton,
  DetailsPanelContent,
  DetailsPanelFooter,
  Panel,
} from "./styles";
import ProposedAnswer from "../ProposedAnswer";

interface KeyQuestionAnswerDetailsPanelProps {
  item: KeyQuestionAnswerFeedItem;
  onClose: () => void;
}

const KeyQuestionAnswerDetailsPanel = ({
  item,
  onClose,
}: KeyQuestionAnswerDetailsPanelProps) => {
  const {
    anchorElement: menuAnchorEl,
    onClick: onClickMenu,
    onClose: onCloseMenu,
    open: menuOpen,
  } = useAnchorElement();

  const updateClip = useUpdateClips();

  const updateAnswerStatus = (status: KeyQuestionAnswerClipStatus) => {
    updateClip.mutate(
      {
        id: item.entityId,
        type: "key_question_answer",
        parentClipId: item.data.questionId,
        sessionId: item.session.id,
        status,
      },
      {
        onError: () => {
          toast.error(
            "Something went wrong and your update could not be saved. Please refresh the page or try again later."
          );
        },
      }
    );
  };

  const handleAccept = () => updateAnswerStatus("user_accept");
  const handleReject = () => updateAnswerStatus("user_reject");

  return (
    <Panel>
      <DetailsPanelHeader item={item} onClose={onClose} />
      <DetailsPanelContent spacing={1}>
        <Typography variant="bodySecondaryReg">
          <Bold>Question:</Bold> {item.data.question}
        </Typography>
        {isProposedAnswer(item.data.status) ? (
          <ProposedAnswer
            canEdit={item.data.canEdit}
            onAccept={handleAccept}
            onReject={handleReject}
          >
            {item.text}
          </ProposedAnswer>
        ) : (
          <Typography variant="bodySecondaryReg">
            <Bold>Answer:</Bold> {item.text}
          </Typography>
        )}
      </DetailsPanelContent>
      <DetailsPanelFooter>
        <ActionsMenuButton onClick={onClickMenu} />
        <Menu open={menuOpen} anchorEl={menuAnchorEl} onClose={onCloseMenu}>
          <Link to={ROUTES.meetingReportById(item.session.id)}>
            <MenuItem>View Report</MenuItem>
          </Link>
        </Menu>
      </DetailsPanelFooter>
    </Panel>
  );
};

export default KeyQuestionAnswerDetailsPanel;
